/**
 * OfflineIndicator Component
 * Shows visual indicator when app is offline or has pending sync actions
 */

import React from "react";
import { useOfflineStatus } from "../../hooks/useOfflineStatus";

const styles = {
  container: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s ease",
  },
  offline: {
    backgroundColor: "#ff4757",
    color: "white",
  },
  pending: {
    backgroundColor: "#ffa502",
    color: "white",
  },
  online: {
    backgroundColor: "#2ed573",
    color: "white",
  },
  icon: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    animation: "pulse 2s infinite",
  },
  iconOffline: {
    backgroundColor: "white",
  },
  iconPending: {
    backgroundColor: "white",
  },
  iconOnline: {
    backgroundColor: "white",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.3)",
    fontSize: "12px",
    fontWeight: "bold",
  },
  retry: {
    color: "white",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.8)",
    borderRadius: "4px",
    cursor: "pointer",
    padding: "2px 6px",
  },
};

// Add keyframe animation via style tag
const pulseKeyframes = `
@keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
}
`;

export function OfflineIndicator({ showOnline = false }) {
  const { isOnline, isElectron, pendingCount, syncError } = useOfflineStatus();
  const retrySync = () => {
    const token = localStorage.getItem("access_token");
    const userId = localStorage.getItem("b") || localStorage.getItem("user_id");
    if (token && userId) {
      window.electronAPI.sync.fullSync(userId, token).catch((error) => {
        console.error("[OfflineIndicator] Retry failed:", error);
      });
    }
  };

  // Don't render if not in Electron
  if (!isElectron) {
    return null;
  }

  // Don't render if online and showOnline is false and no pending actions
  if (isOnline && !showOnline && pendingCount === 0) {
    return null;
  }

  // Determine status and styling
  let statusStyle, iconStyle, text;

  if (!isOnline) {
    statusStyle = { ...styles.container, ...styles.offline };
    iconStyle = { ...styles.icon, ...styles.iconOffline };
    text = "Đang offline";
  } else if (pendingCount > 0) {
    statusStyle = { ...styles.container, ...styles.pending };
    iconStyle = { ...styles.icon, ...styles.iconPending };
    text = syncError ? "Đồng bộ lỗi - còn đơn chờ" : "Có đơn chờ đồng bộ";
  } else {
    statusStyle = { ...styles.container, ...styles.online };
    iconStyle = { ...styles.icon, ...styles.iconOnline };
    text = "Đã kết nối";
  }

  return (
    <>
      <style>{pulseKeyframes}</style>
      <div style={statusStyle} title={syncError || undefined}>
        <div style={iconStyle}></div>
        <span>{text}</span>
        {pendingCount > 0 && <span style={styles.badge}>{pendingCount}</span>}
        {isOnline && pendingCount > 0 && (
          <button type="button" style={styles.retry} onClick={retrySync}>
            Thử lại
          </button>
        )}
      </div>
    </>
  );
}

export default OfflineIndicator;
